import React from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { PageHero } from "@/pages/public/HowItWorks";

const COPY = {
  en: {
    heroEy: "The team",
    heroT: "Builders. Not bankers.",
    heroL:
      "A small, senior team of finance operators and data-center engineers, spread between Europe and Southeast Asia. Close to the ground in Batam — where the compute actually runs.",
    foundingTeam: "Founding team",
    disclaimer:
      "Team profiles reflect the current founding team. Additional hires and technical partners will be announced as they are confirmed.",
    members: [
      {
        name: "Doudou David",
        role: "CEO — Strategy Intelligence Solutions",
        location: "London, United Kingdom",
        bio:
          "Multi-entrepreneur. 25 years of experience in finance. Specialist in Financial Intelligence. Structures the investment vehicle, oversees capital allocation and investor relations for Titan Infra.",
        img: "/team/david-doudou.png",
      },
      {
        name: "Patrick Willems",
        role: "Business Development Engineer — AI Infrastructure & Data Centers",
        location: "Brussels, Belgium",
        bio:
          "12+ years bridging commercial and engineering teams across European data-center operators. Drives partnerships with GPU vendors, colocation providers and enterprise AI customers.",
        img:
          "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=800&q=80",
      },
      {
        name: "Ayu Wijaya",
        role: "Head of Indonesia — Europe Operations",
        location: "Batam, Indonesia",
        bio:
          "Born and raised in Batam. 10 years operating between ASEAN industrial parks and European partners. Manages relations with the Batam Free Zone Authority (BP Batam), BKPM and local suppliers.",
        img: "/team/ayu-wijaya.png",
      },
      {
        name: "Wei Lin",
        role: "Chief Technology Officer",
        location: "Singapore",
        bio:
          "18 years in high-density data-center architecture across APAC. Deployed 100+ MW of AI-ready capacity in Singapore, Japan and Indonesia. Expert in liquid cooling and GPU cluster orchestration.",
        img: "/team/wei-lin.png",
      },
    ],
  },
  fr: {
    heroEy: "L'équipe",
    heroT: "Des bâtisseurs. Pas des banquiers.",
    heroL:
      "Une équipe restreinte et senior, mêlant profils finance et ingénieurs data-center, répartie entre l'Europe et l'Asie du Sud-Est. Proche du terrain à Batam — là où tourne réellement le calcul.",
    foundingTeam: "Équipe fondatrice",
    disclaimer:
      "Les profils présentés correspondent à l'équipe fondatrice actuelle. Les recrutements complémentaires et les partenaires techniques seront annoncés au fur et à mesure de leur confirmation.",
    members: [
      {
        name: "Doudou David",
        role: "CEO — Strategy Intelligence Solutions",
        location: "Londres, Royaume-Uni",
        bio:
          "Multi-entrepreneur. 25 ans d'expérience en finance. Spécialiste en Financial Intelligence. Structure le véhicule d'investissement, supervise l'allocation du capital et les relations investisseurs de Titan Infra.",
        img: "/team/david-doudou.png",
      },
      {
        name: "Patrick Willems",
        role: "Business Development Engineer — Infrastructures IA & Data Centers",
        location: "Bruxelles, Belgique",
        bio:
          "12+ ans à interfacer les équipes commerciales et techniques d'opérateurs data-center européens. Pilote les partenariats avec les fournisseurs GPU, les acteurs de colocation et les clients IA en entreprise.",
        img:
          "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=800&q=80",
      },
      {
        name: "Ayu Wijaya",
        role: "Head of Indonesia — Europe Operations",
        location: "Batam, Indonésie",
        bio:
          "Née et élevée à Batam. 10 ans à opérer entre les parcs industriels ASEAN et les partenaires européens. Gère les relations avec la Batam Free Zone Authority (BP Batam), le BKPM et les fournisseurs locaux.",
        img: "/team/ayu-wijaya.png",
      },
      {
        name: "Wei Lin",
        role: "Chief Technology Officer",
        location: "Singapour",
        bio:
          "18 ans en architecture de data centers haute densité en Asie-Pacifique. Plus de 100 MW de capacité IA déployés à Singapour, au Japon et en Indonésie. Expert en refroidissement liquide et orchestration de clusters GPU.",
        img: "/team/wei-lin.png",
      },
    ],
  },
  es: {
    heroEy: "El equipo",
    heroT: "Constructores. No banqueros.",
    heroL:
      "Un equipo reducido y sénior de operadores financieros e ingenieros de data-center, repartido entre Europa y el sudeste asiático. Cercano al terreno en Batam — donde realmente corre el cálculo.",
    foundingTeam: "Equipo fundador",
    disclaimer:
      "Los perfiles presentados corresponden al equipo fundador actual. Las contrataciones adicionales y los socios técnicos se anunciarán a medida que se confirmen.",
    members: [
      {
        name: "Doudou David",
        role: "CEO — Strategy Intelligence Solutions",
        location: "Londres, Reino Unido",
        bio:
          "Multi-emprendedor. 25 años de experiencia en finanzas. Especialista en Financial Intelligence. Estructura el vehículo de inversión, supervisa la asignación de capital y las relaciones con inversores de Titan Infra.",
        img: "/team/david-doudou.png",
      },
      {
        name: "Patrick Willems",
        role: "Business Development Engineer — Infraestructuras IA y Data Centers",
        location: "Bruselas, Bélgica",
        bio:
          "Más de 12 años conectando equipos comerciales y de ingeniería en operadores europeos de data centers. Impulsa asociaciones con proveedores de GPU, colocación y clientes IA empresariales.",
        img:
          "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=800&q=80",
      },
      {
        name: "Ayu Wijaya",
        role: "Head of Indonesia — Europe Operations",
        location: "Batam, Indonesia",
        bio:
          "Nacida y criada en Batam. 10 años operando entre parques industriales ASEAN y socios europeos. Gestiona las relaciones con la Batam Free Zone Authority (BP Batam), el BKPM y proveedores locales.",
        img: "/team/ayu-wijaya.png",
      },
      {
        name: "Wei Lin",
        role: "Chief Technology Officer",
        location: "Singapur",
        bio:
          "18 años en arquitectura de data centers de alta densidad en Asia-Pacífico. Más de 100 MW de capacidad IA desplegados en Singapur, Japón e Indonesia. Experto en refrigeración líquida y orquestación de clústeres GPU.",
        img: "/team/wei-lin.png",
      },
    ],
  },
};

export default function Team() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;

  return (
    <>
      <PageHero eyebrow={c.heroEy} title={c.heroT} lead={c.heroL} />

      <section className="bg-white py-24 md:py-32" data-testid="public-team-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <p className="text-[11px] tracking-[0.22em] uppercase text-zinc-500 mb-8 font-medium">
            {c.foundingTeam}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-testid="public-team-grid">
            {c.members.map((m, i) => (
              <motion.article
                key={m.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                data-testid={`public-team-member-${i}`}
                className="group border border-black/5 bg-zinc-50 overflow-hidden flex flex-col"
              >
                <div className="aspect-[3/4] overflow-hidden bg-zinc-200 relative">
                  <img
                    src={m.img}
                    alt={m.name}
                    className="w-full h-full object-cover object-[center_20%] grayscale group-hover:grayscale-0 transition-all duration-1000 ease-out"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute bottom-3 left-4 right-4 flex items-center gap-1.5 text-[11px] text-white/85">
                    <MapPin className="w-3 h-3" />
                    <span>{m.location}</span>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <p className="font-display text-xl tracking-tight" data-testid={`public-team-member-name-${i}`}>
                    {m.name}
                  </p>
                  <p className="mt-1.5 text-[10px] uppercase tracking-widest text-emerald-600 font-medium" data-testid={`public-team-member-role-${i}`}>
                    {m.role}
                  </p>
                  <p className="mt-4 text-[13px] leading-relaxed text-zinc-600">
                    {m.bio}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>

          <p className="mt-14 text-[11px] leading-relaxed text-zinc-500 max-w-3xl" data-testid="public-team-disclaimer">
            {c.disclaimer}
          </p>
        </div>
      </section>
    </>
  );
}
